// @ts-check
/**
 * Gives an accessible name to iframes that a third-party app injects WITHOUT a `title`.
 *
 * Lighthouse / axe `frame-title` (WCAG 4.1.2, level A) fails on every page of this store because of the
 * Google & YouTube app's store badge: `<iframe id="merchantwidgetiframe">` is appended to `<body>` by
 * Google's own script, after load, with no title and no aria-label. The theme cannot change that markup,
 * so this sets the title once the element exists. It only touches an iframe that still has no title,
 * so if the app ever starts shipping one, the app's wins.
 *
 * Polls instead of observing the DOM: the iframe shows up a few seconds after load, and a
 * MutationObserver on the whole document would fire for every mutation of the visit (that is what
 * assets/wishlist-app-card-button.js used to do, see the "Mobile follow-up" note in CLAUDE.md).
 * 24 checks 750 ms apart cost nothing, and the loop ends as soon as every frame has been named.
 */

const POLL_MS = 750;
const MAX_POLLS = 24;

/** @type {Array<{ selector: string, translationKey: string }>} */
const FRAMES = [{ selector: 'iframe#merchantwidgetiframe', translationKey: 'google_store_badge' }];

/**
 * @returns {boolean} true while at least one frame has not appeared yet
 */
function nameFrames() {
  const translations = /** @type {Record<string, string>} */ (Theme.translations);
  let waiting = false;

  for (const { selector, translationKey } of FRAMES) {
    const frame = document.querySelector(selector);
    if (!(frame instanceof HTMLIFrameElement)) {
      waiting = true;
      continue;
    }
    if (!frame.title && translations[translationKey]) frame.title = translations[translationKey];
  }

  return waiting;
}

let polls = 0;

function poll() {
  if (nameFrames() && ++polls < MAX_POLLS) setTimeout(poll, POLL_MS);
}

if (document.readyState === 'complete') {
  poll();
} else {
  window.addEventListener('load', poll, { once: true });
}
